"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.App = App;
const app_consensus_1 = require("@branch-ts/app-consensus");
const debug_consensus_1 = require("@branch-ts/debug-consensus");
const biome_ai_web_1 = require("@branch-ts/biome-ai-web");
const browserCRX_1 = require("@project/verset_consensus/src/browserCRX");
const practice_auth3_web_1 = require("@branch-ts/practice-auth3-web");
const web_1 = require("@branch-ts/web");
const web_consensus_1 = require("@branch-ts/web-consensus");
require("@project/verset_consensus");
require("@branch-ts/web-consensus/src/theme/theme.business.next/spec.html.R");
const service_debug_1 = require("./service.debug");
const service_VersetBilling_1 = require("./service.VersetBilling");
const service_VersetBucket_1 = require("./service.VersetBucket");
const service_VersetBusinessGuide_1 = require("./service.VersetBusinessGuide");
const service_VersetIC_1 = require("./service.VersetIC");
const service_VersetIDPS_1 = require("./service.VersetIDPS");
const service_VersetGuild_1 = require("./service.VersetGuild");
const service_VersetOmniBar_1 = require("./service.VersetOmniBar");
const service_VersetOmni_1 = require("./service.VersetOmni");
const service_VersetProbe_1 = require("./service.VersetProbe");
const service_VersetProbeHint_1 = require("./service.VersetProbeHint");
const service_VersetRequireConnection_1 = require("./service.VersetRequireConnection");
const service_VersetRequireLogin_1 = require("./service.VersetRequireLogin");
const service_VersetScopedContent_1 = require("./service.VersetScopedContent");
const service_VersetSeqop_1 = require("./service.VersetSeqop");
const service_Verset_1 = require("./service.Verset");
const service_VersetStickyNotification_1 = require("./service.VersetStickyNotification");
const service_VersetSubscriptionHint_1 = require("./service.VersetSubscriptionHint");
const service_VersetTheme_1 = require("./service.VersetTheme");
const service_VersetVerse_1 = require("./service.VersetVerse");
const service_VersetSingular_1 = require("./service.VersetSingular");
const service_VersetWorkerFacility_1 = require("./service.VersetWorkerFacility");
const site_1 = require("./site");
require("./icon");
function resolveHoldingPageURL(currentLocation) {
    const localeFromPath = currentLocation.pathname.match(/^\/(en|ja)(?:\/|$)/)?.[1];
    const locale = localeFromPath || (globalThis.navigator?.language?.toLowerCase().startsWith("ja") ? "ja" : "en");
    return `https://verset.io/${locale}/e/not-now/`;
}
const publicAppTestAccessStorageKey = "verset.publicAppTestAccess";
function hasPublicAppTestAccess(currentLocation) {
    if (currentLocation.href.includes("test")) {
        try {
            globalThis.localStorage.setItem(publicAppTestAccessStorageKey, "true");
        }
        catch {
            // Continue this visit when storage is unavailable; the query flag remains authoritative.
        }
        return true;
    }
    try {
        return globalThis.localStorage.getItem(publicAppTestAccessStorageKey) === "true";
    }
    catch {
        return false;
    }
}
function redirectPublicAppVisit() {
    if (!globalThis.location) {
        return;
    }
    const currentLocation = globalThis.location;
    if (currentLocation.hostname === "uni.verset.io" && !hasPublicAppTestAccess(currentLocation)) {
        currentLocation.replace(resolveHoldingPageURL(currentLocation));
    }
}
function configureDebug() {
    if (!globalThis.location?.href.includes("debug")) {
        debug_consensus_1.Debug.disable();
    }
}
function App(option = {}) {
    configureDebug();
    const logoutStatus = web_consensus_1.LogoutMechanism.consumeRedirectStatus();
    if (logoutStatus === "complete")
        web_consensus_1.CommonUI.toast("Logged out of Verset.");
    if (logoutStatus === "incomplete") {
        void web_consensus_1.CommonUI.show({
            title: "Logged out with a warning",
            text: "This page has left your signed-in session. Some storage cleanup or server-session revocation could not be confirmed. Close any remaining Verset tabs; if the warning persists, clear this site's data and check your server sessions.",
        }).catch(error => console.warn("Could not display logout warning", error));
    }
    redirectPublicAppVisit();
    const declaredEndpoint = app_consensus_1.VAS.appInfos?.VersetWebapp?.endpoints?.server;
    const declaredWorkerUrl = app_consensus_1.VAS.appInfos?.VersetWebapp?.configs?.worker;
    const metaOrCachedEndpoint = web_1.Endpoint.getEndpoints().server;
    const locationEndpoint = globalThis.location ? `${globalThis.location.protocol === "https:" ? "wss" : "ws"}://${globalThis.location.host}` : "ws://localhost:9090";
    const defaultEndpoint = declaredEndpoint || metaOrCachedEndpoint || locationEndpoint;
    const resolvedEndpoint = option.apiEndpoint || defaultEndpoint;
    const browserFeature = (0, biome_ai_web_1.BrowserCRXFeature)(browserCRX_1.browserCRXConfig);
    const storageVendor = "Verset";
    const sessionStorageProvider = new web_consensus_1.SessionStorageRootStorage(storageVendor);
    const unlimitedStorageProvider = new web_consensus_1.IndexedDBRootStorage(storageVendor);
    const localStorageProvider = new web_consensus_1.LocalStorageRootStorage(storageVendor);
    const app = new app_consensus_1.AppContext({
        vendor: "Verset",
        name: "VersetWebapp",
        services: {
            DebugService: service_debug_1.DebugService,
            SessionService: new practice_auth3_web_1.SessionService({ ignoreSavedAuth: !!logoutStatus }),
            NavigationService: web_1.NavigationService,
            VersetRequireConnectionService: new service_VersetRequireConnection_1.VersetRequireConnectionService({
                apiEndpoint: resolvedEndpoint,
            }),
            VersetRequireLoginService: new service_VersetRequireLogin_1.VersetRequireLoginService({
                localStorageProvider,
                sessionStorageProvider,
                unlimitedStorageProvider,
            }),
            VersetWorkerFacilityService: new service_VersetWorkerFacility_1.VersetWorkerFacilityService({
                sharedWorkerUrl: option.workerUrl || declaredWorkerUrl || "/static/entry.SharedWorker.js",
                // One deliberate cutover from the legacy raw-wire worker and its immutable caches.
                // Keep stable for compatible releases; a bump creates a separate arbitrator generation.
                sharedWorkerVersion: option.workerVersion ?? "connection-v1-20261030",
            }),
            VersetThemeService: service_VersetTheme_1.VersetThemeService,
            VersetStickyNotificationService: service_VersetStickyNotification_1.VersetStickyNotificationService,
            VersetSubscriptionHintService: service_VersetSubscriptionHint_1.VersetSubscriptionHintService,
            VersetProbeHintService: service_VersetProbeHint_1.VersetProbeHintService,
            VersetSingularService: service_VersetSingular_1.VersetSingularService,
            wireVersetHomeNavigation: app_consensus_1.AppContext.wire({
                deps: {
                    NavigationService: web_1.NavigationService,
                },
                wire: async (deps) => {
                    web_consensus_1.UIMechanism.setHome(() => {
                        deps.NavigationService.handleUrl("/");
                    });
                },
            }),
            VersetSeqopService: service_VersetSeqop_1.VersetSeqopService,
            VersetScopedContentService: service_VersetScopedContent_1.VersetScopedContentService,
            VersetVerseService: service_VersetVerse_1.VersetVerseService,
            VersetBucketService: service_VersetBucket_1.VersetBucketService,
            VersetBillingService: service_VersetBilling_1.VersetBillingService,
            VersetBusinessGuideService: service_VersetBusinessGuide_1.VersetBusinessGuideService,
            VersetIDPSService: new service_VersetIDPS_1.VersetIDPSService({ browser: browserFeature.browser }),
            VersetProbeService: service_VersetProbe_1.VersetProbeService,
            VersetGuildService: service_VersetGuild_1.VersetGuildService,
            VersetICService: new service_VersetIC_1.VersetICService({ browser: browserFeature.browser }),
            VersetOmniService: service_VersetOmni_1.VersetOmniService,
            VersetOmniBarService: service_VersetOmniBar_1.VersetOmniBarService,
            VersetService: new service_Verset_1.VersetService({ browserFeature }),
            SiteEntryService: site_1.SiteEntryService,
        },
        warehouses: {
            default: new app_consensus_1.InMemoryWarehouseDriver({
                name: "default",
                passthrough: true,
            }),
        },
    });
    return app;
}
